/**
 * StatusBadge.tsx
 * ─────────────────────────────────────────────────────────────
 * Cognisun CRM — Universal Status Badge / Pill
 *
 * THE ONLY badge/pill component in the codebase.
 * Replace all ad-hoc inline status spans with this component.
 *
 * DESIGN CONTRACT:
 *   • 9999px border-radius (rounded-full)  — var(--ds-radius-badge)
 *   • 11px text, font-weight 500           — ds-badge base class
 *   • 1px semantic border                  — per variant
 *   • Optional leading dot (2×2 circle)    — dot={true} default
 *
 * USAGE — auto-map (pass the raw status string):
 *   <StatusBadge status="Approved" />
 *   <StatusBadge status="Pending" />
 *   <StatusBadge status="In Progress" />
 *
 * USAGE — explicit variant:
 *   <StatusBadge variant="success">Paid</StatusBadge>
 *   <StatusBadge variant="danger" dot={false}>Failed</StatusBadge>
 *   <StatusBadge variant="primary">New</StatusBadge>
 *
 * USAGE — no dot:
 *   <StatusBadge status="Completed" dot={false} />
 *
 * VARIANTS:
 *   success  → green  (Approved, Completed, Active, Paid)
 *   warning  → amber  (Pending, Processing, On Hold)
 *   danger   → red    (Rejected, Failed, Inactive, Overdue)
 *   info     → blue   (In Progress, In Review)
 *   primary  → brand  (New, Draft, Selected)
 *   neutral  → gray   (To Do, None, —)
 *
 * AUTO-MAPPED STATUS STRINGS:
 *   "Approved" | "Completed" | "Active" | "Paid"       → success
 *   "Pending"  | "Processing" | "On Hold" | "Draft"    → warning
 *   "Rejected" | "Failed" | "Inactive" | "Overdue"     → danger
 *   "In Progress" | "In Review" | "Running"            → info
 *   "To Do" | "None" | "—"                             → neutral
 */

import React from 'react';
import { cn } from '@/components/ui/utils';

// ─── Variant Types ────────────────────────────────────────────────────────────

export type StatusVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'primary'
  | 'neutral';

// ─── Variant → className map ──────────────────────────────────────────────────
// All values reference the DS palette from design-tokens.ts / theme.css

const VARIANT_CLASSES: Record<StatusVariant, string> = {
  success:  'bg-green-50   text-[#047857] border-green-200',
  warning:  'bg-amber-50   text-[#92400E] border-amber-200',
  danger:   'bg-red-50     text-[#B91C1C] border-red-200',
  info:     'bg-[#EFF6FF]  text-[#1D4ED8] border-[#BFDBFE]',
  primary:  'bg-[#EBF6FD]  text-[#008ED3] border-[#C5E4F3]',
  neutral:  'bg-gray-100   text-gray-600  border-gray-200',
};

const DOT_CLASSES: Record<StatusVariant, string> = {
  success:  'bg-green-500',
  warning:  'bg-amber-500',
  danger:   'bg-red-500',
  info:     'bg-blue-500',
  primary:  'bg-[#008ED3]',
  neutral:  'bg-gray-400',
};

// ─── Status string → Variant resolver ────────────────────────────────────────

const STATUS_MAP: Record<string, StatusVariant> = {
  // ── Success
  'Approved':     'success',
  'Completed':    'success',
  'Active':       'success',
  'Paid':         'success',
  'Present':      'success',
  'On Time':      'success',
  // ── Warning
  'Pending':      'warning',
  'Queued':       'warning',
  'Processing':   'warning',
  'On Hold':      'warning',
  'Draft':        'warning',
  'Partial':      'warning',
  'Absent':       'warning',
  // ── Danger
  'Rejected':     'danger',
  'Failed':       'danger',
  'Inactive':     'danger',
  'Overdue':      'danger',
  'Cancelled':    'danger',
  'Late':         'danger',
  // ── Info
  'In Progress':  'info',
  'In Review':    'info',
  'Running':      'info',
  'Open':         'info',
  // ── Neutral
  'To Do':        'neutral',
  'Idle':         'neutral',
  'Skipped':      'neutral',
  'None':         'neutral',
  '—':            'neutral',
  'N/A':          'neutral',
};

function resolveVariant(status?: string, explicit?: StatusVariant): StatusVariant {
  if (explicit) return explicit;
  if (status && STATUS_MAP[status]) return STATUS_MAP[status];
  return 'neutral';
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface StatusBadgeProps {
  /**
   * Pass the raw status string for auto-mapping.
   * Recognized values: Approved, Pending, Rejected, Completed,
   * Processing, In Progress, In Review, To Do, Active, Inactive,
   * Paid, Failed, Draft, On Hold, etc.
   */
  status?: string;

  /**
   * Override auto-mapping with an explicit variant.
   * Useful when the label differs from the status key.
   */
  variant?: StatusVariant;

  /** Show the leading dot indicator. Default: true */
  dot?: boolean;

  /**
   * Custom label. If omitted, `status` prop is used as the label.
   * Useful when you want a different display name than the status key.
   * @example <StatusBadge status="Approved" variant="success">Verified</StatusBadge>
   */
  children?: React.ReactNode;

  /** Extra className overrides */
  className?: string;
}

export function StatusBadge({
  status,
  variant,
  dot = true,
  children,
  className = '',
}: StatusBadgeProps) {
  const resolved = resolveVariant(status, variant);
  const label    = children ?? status ?? '';

  return (
    <span
      data-component="status-badge"
      data-variant={resolved}
      data-status={status}
      className={cn(
        // Base pill structure (from theme.css .ds-badge)
        'inline-flex items-center gap-1 rounded-full',
        'text-[11px] font-medium leading-[1.45]',
        'border whitespace-nowrap select-none',
        'px-2 py-0.5',
        // Semantic color
        VARIANT_CLASSES[resolved],
        className
      )}
    >
      {dot && (
        <span
          aria-hidden="true"
          className={cn('w-1.5 h-1.5 rounded-full shrink-0', DOT_CLASSES[resolved])}
        />
      )}
      {label}
    </span>
  );
}
